// @ts-check

import { setupServiceWorkerReloadWhenIdle } from "./sw-reload.js";

const debugTapThreshold = 7;
const debugTapWindowMs = 1500;
const debugTapState = { count: 0, lastTapAt: 0 };
const dialogClassName = "myDialogPrompt";
const dialogStyleId = "dialog-styles-injected";

class Safe extends String {
  /**
   * @param {string} value
   */
  constructor(value) {
    super(value);
  }
}

/**
 * @param {string} value
 * @returns {Safe}
 */
function safe(value) {
  return new Safe(value);
}

/**
 * @param {TemplateStringsArray} strings
 * @param {...unknown} values
 * @returns {HTMLElement | null}
 */
function htl(strings, ...values) {
  /**
   * @param {string} html
   * @returns {DocumentFragment}
   */
  function renderHtml(html) {
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    return document.importNode(template.content, true);
  }

  /**
   * @param {DocumentFragment} fragment
   * @returns {HTMLElement | null}
   */
  function extractFragment(fragment) {
    if (fragment.firstChild === null) {
      return null;
    }

    const firstChild = fragment.firstChild;
    if (
      firstChild === fragment.lastChild &&
      firstChild instanceof HTMLElement
    ) {
      return fragment.removeChild(firstChild);
    }

    const wrapper = document.createElement("div");
    wrapper.appendChild(fragment);
    return wrapper;
  }

  /**
   * Converts template inputs into HTML at the rendering boundary.
   * @param {unknown} value
   * @returns {string}
   */
  function escapeHtmlChars(value) {
    if (value instanceof Safe) {
      return value.toString();
    }

    if (value instanceof HTMLElement) {
      return value.outerHTML;
    }

    if (value instanceof DocumentFragment) {
      const wrapper = document.createElement("div");
      wrapper.appendChild(value.cloneNode(true));
      return wrapper.innerHTML;
    }

    if (value === null || value === undefined) {
      return "";
    }

    return String(value).replace(/[<>'"]/g, function (char) {
      return `&#${char.charCodeAt(0)};`;
    });
  }

  /** @type {string[]} */
  const processedValues = [];

  for (let i = 0, total = values.length; i < total; i++) {
    const flattenedValue = [values[i]].flat();
    /** @type {string[]} */
    const escapedParts = [];

    for (let j = 0, totalParts = flattenedValue.length; j < totalParts; j++) {
      escapedParts.push(escapeHtmlChars(flattenedValue[j]));
    }

    processedValues.push(escapedParts.join(""));
  }

  let html = strings[0];
  for (let i = 0, total = processedValues.length; i < total; i++) {
    html += processedValues[i] + strings[i + 1];
  }

  return extractFragment(renderHtml(html));
}

/**
 * @returns {HTMLStyleElement}
 */
function injectDialogStyles() {
  const existingStyle = document.getElementById(dialogStyleId);
  if (existingStyle instanceof HTMLStyleElement) {
    return existingStyle;
  }

  const style = document.createElement("style");
  style.id = dialogStyleId;
  style.textContent = `
    .myDialogPrompt {
      border: none;
      border-radius: 0.5rem;
      padding: 1.25rem;
      min-width: 300px;
      max-width: min(42rem, calc(100vw - 2rem));
      box-sizing: border-box;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      background-color: #ffffff;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      margin: auto;
    }

    .myDialogPrompt::backdrop {
      background-color: rgba(17, 24, 39, 0.7);
    }

    .myDialogPrompt form {
      margin: 0;
    }

    .myDialogPrompt .dialog-message {
      margin-bottom: 1.25rem;
      font-size: 0.875rem;
      color: #111827;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .myDialogPrompt .dialog-rich-content {
      display: grid;
      gap: 0.875rem;
      white-space: normal;
    }

    .myDialogPrompt .dialog-rich-title {
      font-size: 1rem;
      font-weight: 700;
      line-height: 1.4;
    }

    .myDialogPrompt .dialog-debug-list {
      display: grid;
      gap: 0.625rem;
      max-height: min(60vh, 28rem);
      overflow: auto;
      padding-right: 0.25rem;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
      font-size: 0.8125rem;
    }

    .myDialogPrompt .dialog-debug-row {
      display: grid;
      grid-template-columns: minmax(0, 9rem) minmax(0, 1fr);
      gap: 0.75rem;
      align-items: start;
    }

    .myDialogPrompt .dialog-debug-label {
      color: #6b7280;
      font-weight: 600;
    }

    .myDialogPrompt .dialog-debug-value {
      overflow-wrap: anywhere;
    }

    .myDialogPrompt .dialog-actions {
      text-align: right;
      margin-top: 1.25rem;
    }

    .myDialogPrompt button {
      cursor: pointer;
      border-radius: 0.375rem;
      padding: 0.5rem 0.75rem;
      font-size: 0.875rem;
      font-weight: 600;
      border: none;
    }

    .myDialogPrompt button.primary {
      background-color: #4f46e5;
      color: #ffffff;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    }

    .myDialogPrompt button.primary:hover {
      background-color: #6366f1;
    }
  `;

  document.head.appendChild(style);
  return style;
}

function removeDialogStyles() {
  const style = document.getElementById(dialogStyleId);
  if (style) {
    style.remove();
  }
}

/**
 * @returns {HTMLDialogElement}
 */
function createDialog() {
  injectDialogStyles();

  const dialogElement = htl`<dialog class="${dialogClassName}"></dialog>`;
  if (!(dialogElement instanceof HTMLDialogElement)) {
    throw new Error("Failed to create dialog element.");
  }

  const dialog = dialogElement;
  document.body.appendChild(dialog);
  return dialog;
}

/**
 * @param {HTMLDialogElement} dialog
 * @returns {void}
 */
function destroyDialog(dialog) {
  dialog.textContent = "";
  dialog.remove();
  removeDialogStyles();
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {string | HTMLElement | DocumentFragment | null} message
 * @returns {void}
 */
function renderAlertDialog(dialog, message) {
  const formElement = htl`
    <form method="dialog">
      <div class="dialog-message">${message}</div>
      <div class="dialog-actions">
        <button type="submit" value="ok" class="primary">OK</button>
      </div>
    </form>
  `;
  if (!(formElement instanceof HTMLFormElement)) {
    throw new Error("Failed to create alert dialog form.");
  }

  const form = formElement;
  dialog.appendChild(form);

  form.addEventListener("submit", function (event) {
    event.preventDefault();
    dialog.close("ok");
  });
}

/**
 * @param {{ message: string | HTMLElement | DocumentFragment | null }} options
 * @returns {Promise<void>}
 */
async function dAlert({ message }) {
  const dialog = createDialog();
  renderAlertDialog(dialog, message);

  return new Promise(function (resolve) {
    dialog.addEventListener("close", function () {
      resolve();
      destroyDialog(dialog);
    });

    dialog.showModal();
  });
}

function renderAppShell() {
  const root = document.getElementById("app");
  if (!(root instanceof HTMLElement)) {
    return;
  }

  root.innerHTML = `
    <div id="appShell" class="bg-white w-full max-w-full h-dvh min-h-full box-border p-5 shadow-lg flex flex-col">
      <div class="relative shrink-0 min-h-10">
        <button id="splitToggle" aria-label="Split laps into four panels" title="Split laps" class="absolute right-0 top-0 bg-gray-100 hover:bg-gray-200 active:translate-y-px text-gray-700 border border-gray-300 rounded-lg w-10 h-10 text-lg font-semibold cursor-pointer transition-colors duration-200 touch-manipulation">⊞</button>
      </div>
      <div class="text-5xl font-bold text-center tabular-nums mt-1 mb-4 text-gray-900 shrink-0" id="display">00:00.00</div>
      <div class="flex gap-2.5 justify-center mb-3 shrink-0">
        <button id="startStop" class="bg-blue-600 hover:bg-blue-500 active:translate-y-px text-white border-0 rounded-lg px-6 py-3 text-base font-semibold cursor-pointer min-w-25 transition-colors duration-200 touch-manipulation">Start</button>
        <button id="lap" disabled class="bg-green-600 hover:bg-green-500 active:translate-y-px text-white border-0 rounded-lg px-6 py-3 text-base font-semibold cursor-pointer min-w-25 transition-colors duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed touch-manipulation">Lap</button>
        <span id="resetDebugTarget">
          <button id="reset" disabled class="bg-red-600 hover:bg-red-500 active:translate-y-px text-white border-0 rounded-lg px-6 py-3 text-base font-semibold cursor-pointer min-w-25 transition-colors duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed touch-manipulation">Reset</button>
        </span>
      </div>
      <div id="lapTapArea" class="mt-3 border-t border-gray-200 pt-2.5 touch-manipulation flex-1 min-h-0 overflow-y-auto overscroll-contain">
        <div class="font-bold text-xl mb-2.5 touch-manipulation">Laps</div>
        <div class="grid grid-cols-[2.75rem_minmax(0,1fr)_minmax(0,1fr)] gap-2 p-2.5 border-b-2 border-gray-300 pb-1.5 font-bold touch-manipulation">
          <div>Lap #</div>
          <div>Lap Time</div>
          <div>Total Time</div>
        </div>
        <div id="laps-container">
          <div id="no-laps-message" class="text-center text-gray-500 italic py-5">No laps recorded</div>
        </div>
      </div>
    </div>
  `;
}

function main() {
  renderAppShell();
  // DOM elements
  const displayElement = document.getElementById("display");
  const appShellElement = document.getElementById("appShell");
  const startStopButton = document.getElementById("startStop");
  const lapButton = document.getElementById("lap");
  const resetButton = document.getElementById("reset");
  const resetDebugTargetElement = document.getElementById("resetDebugTarget");
  const splitToggleButton = document.getElementById("splitToggle");
  const lapTapArea = document.getElementById("lapTapArea");
  const lapsContainer = document.getElementById("laps-container");
  const hasRequiredElements =
    displayElement instanceof HTMLElement &&
    appShellElement instanceof HTMLElement &&
    startStopButton instanceof HTMLButtonElement &&
    lapButton instanceof HTMLButtonElement &&
    resetButton instanceof HTMLButtonElement &&
    resetDebugTargetElement instanceof HTMLElement &&
    splitToggleButton instanceof HTMLButtonElement &&
    lapTapArea instanceof HTMLElement &&
    lapsContainer instanceof HTMLElement;
  if (!hasRequiredElements) {
    return;
  }
  const display = displayElement;
  const startStop = startStopButton;
  const lap = lapButton;
  const reset = resetButton;
  const resetDebugTarget = resetDebugTargetElement;
  const splitToggle = splitToggleButton;
  const tapArea = lapTapArea;
  const laps = lapsContainer;

  // Stopwatch state
  let isRunning = false;
  let startTime = 0;
  let elapsedTime = 0;
  let isSplitMode = false;
  /** A recorded lap uses whole milliseconds so displayed durations add exactly.
   * @typedef {{ number: number, duration: number, total: number }} LapRecord
   */
  /** @type {number | null} */
  let expandedPanelIndex = null;
  let isSelectingLaps = false;
  /** @typedef {{ lastLapTime: number, lapCounter: number, lapsElement: HTMLElement | null, records: LapRecord[], selected: Set<number> }} PanelState */
  /** @returns {PanelState} */
  function createPanelState() {
    return {
      lastLapTime: 0,
      lapCounter: 0,
      lapsElement: null,
      records: [],
      selected: new Set(),
    };
  }

  const singlePanelStates = [createPanelState()];
  const splitPanelStates = [
    createPanelState(),
    createPanelState(),
    createPanelState(),
    createPanelState(),
  ];
  let panelStates = singlePanelStates;
  panelStates[0].lapsElement = laps;
  /** @type {number | null} */
  let animationFrameId = null;
  /** @type {WakeLockSentinel | null} */
  let wakeLock = null;
  /** @type {Promise<void> | null} */
  let wakeLockRequestPromise = null;
  /** @type {HTMLVideoElement | null} */
  let keepAwakeVideo = null;
  let shouldKeepAwake = false;
  /** @type {Map<number, { x: number, y: number }>} */
  const lapTapStarts = new Map();
  /** @type {WeakMap<HTMLElement, number>} */
  const panelFlashTimeouts = new WeakMap();

  setResetDisabled(reset.disabled);

  function getNoLapsHtml() {
    return '<div class="no-laps-message text-center text-gray-500 italic py-5">No laps recorded</div>';
  }

  /**
   * @param {number} panelIndex
   * @returns {string}
   */
  function getLapsContainerId(panelIndex) {
    let result = "laps-container";
    if (panelIndex > 0) {
      result = `laps-container-${panelIndex}`;
    }
    return result;
  }

  /**
   * @param {number} panelIndex
   * @returns {string}
   */
  function getLapPanelHtml(panelIndex) {
    const containerId = getLapsContainerId(panelIndex);
    const isCompact = isSplitMode && expandedPanelIndex === null;
    const canReview = !isRunning && elapsedTime > 0;
    const titleText = isSplitMode ? `Table ${panelIndex + 1}` : "Laps";
    const expandHtml = isCompact
      ? `<button type="button" class="lap-action ${canReview ? "" : "lap-action-unavailable"}" ${canReview ? "" : "disabled"} data-expand-panel="${panelIndex}" aria-label="Expand Table ${panelIndex + 1}">Expand <span aria-hidden="true">↗</span></button>`
      : "";
    const backHtml = expandedPanelIndex !== null
      ? '<button type="button" id="backToTables" class="lap-action">← All tables</button>'
      : "";
    const canSum = canReview && panelStates[panelIndex].records.length > 0;
    const sumHtml = !isCompact
      ? `<button type="button" id="sumLaps" class="lap-action ${canSum ? "" : "lap-action-unavailable"}" ${canSum ? "" : "disabled"} aria-pressed="${isSelectingLaps}">${isSelectingLaps ? "Done" : "Sum laps"}</button>`
      : "";
    const titleHtml = `<div class="lap-toolbar ${isCompact ? "text-xs" : "text-xl"}">${backHtml}<h2 class="font-bold">${titleText}</h2>${expandHtml}${sumHtml}</div>`;
    const selectionHtml =
      !isCompact && isSelectingLaps
        ? `<div class="lap-summary"><div><div id="selectionCount" class="text-sm text-blue-900">0 laps selected</div><output id="selectionTotal" class="text-2xl font-bold tabular-nums" aria-live="polite" aria-atomic="true" aria-label="Selected lap total">00:00.000</output></div><button type="button" id="clearSelection" class="lap-action">Clear</button><p class="text-sm text-blue-900 col-span-2">Tap rows to select or deselect laps.</p></div>`
        : "";
    const lapNumberTitle = isCompact ? "#" : "Lap #";
    const lapTitle = isCompact ? "Lap" : "Lap Time";
    const totalTitle = isCompact ? "Total" : "Total Time";
    const gridColumnsClass = isCompact
      ? "grid-cols-[2rem_minmax(0,1fr)_minmax(0,1fr)]"
      : "grid-cols-[2.75rem_minmax(0,1fr)_minmax(0,1fr)]";
    const textSizeClass = isCompact ? "text-xs" : "text-sm";
    const result = `
      ${titleHtml}
      ${selectionHtml}
      <div class="grid ${gridColumnsClass} gap-2 p-1.5 border-b-2 border-gray-300 pb-1.5 font-bold touch-manipulation ${textSizeClass} tabular-nums">
        <div class="min-w-0">${lapNumberTitle}</div>
        <div class="min-w-0">${lapTitle}</div>
        <div class="min-w-0">${totalTitle}</div>
      </div>
      <div id="${containerId}" class="${textSizeClass} tabular-nums">
        ${getNoLapsHtml()}
      </div>
    `;
    return result;
  }

  function resetPanelStates() {
    const allPanels = [...singlePanelStates, ...splitPanelStates];
    for (let i = 0, total = allPanels.length; i < total; i++) {
      const panel = allPanels[i];
      panel.lastLapTime = 0;
      panel.lapCounter = 0;
      panel.lapsElement = null;
      panel.records = [];
      panel.selected.clear();
    }
  }

  function refreshPanelElements() {
    for (let i = 0, total = panelStates.length; i < total; i++) {
      const lapsContainerId = getLapsContainerId(i);
      const panelLapsElement = document.getElementById(lapsContainerId);
      if (panelLapsElement instanceof HTMLElement) {
        panelStates[i].lapsElement = panelLapsElement;
      }
    }
  }

  function renderLapArea() {
    if (isSplitMode && expandedPanelIndex === null) {
      tapArea.className =
        "touch-manipulation flex-1 min-h-0 grid grid-cols-2 grid-rows-2 overflow-hidden -mx-3";
      tapArea.innerHTML = "";

      for (let i = 0, total = panelStates.length; i < total; i++) {
        const panelElement = document.createElement("div");
        const touchClass = isRunning ? "touch-none" : "touch-manipulation";
        panelElement.className = `split-panel min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain border border-gray-200 p-1 ${touchClass}`;
        panelElement.dataset.panelIndex = String(i);
        panelElement.innerHTML = getLapPanelHtml(i);
        tapArea.appendChild(panelElement);
      }
    } else {
      const panelIndex = expandedPanelIndex ?? 0;
      tapArea.className =
        "mt-3 border-t border-gray-200 pt-2.5 touch-manipulation flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain";
      tapArea.innerHTML = getLapPanelHtml(panelIndex);
    }

    for (let i = 0, total = panelStates.length; i < total; i++) {
      panelStates[i].lapsElement = null;
    }
    refreshPanelElements();
    for (let i = 0, total = panelStates.length; i < total; i++) {
      const panel = panelStates[i];
      if (!panel.lapsElement || panel.records.length === 0) continue;
      panel.lapsElement.textContent = "";
      for (let j = panel.records.length - 1; j >= 0; j--) {
        panel.lapsElement.appendChild(createLapRow(i, panel.records[j]));
      }
    }
    updateSelectionSummary();
  }

  /**
   * @param {number} panelIndex
   * @param {LapRecord} record
   * @returns {HTMLElement}
   */
  function createLapRow(panelIndex, record) {
    const isCompact = isSplitMode && expandedPanelIndex === null;
    const isSelectable = !isRunning && isSelectingLaps && !isCompact;
    const row = document.createElement(isSelectable ? "button" : "div");
    const columns = isCompact
      ? "grid-cols-[2rem_minmax(0,1fr)_minmax(0,1fr)]"
      : "grid-cols-[2.75rem_minmax(0,1fr)_minmax(0,1fr)]";
    row.className = `lap-row grid ${columns} gap-2 p-1.5 border-b border-gray-200 text-left w-full tabular-nums`;
    row.dataset.lapNumber = String(record.number);
    if (row instanceof HTMLButtonElement) {
      row.type = "button";
      row.classList.add("lap-selectable");
      row.setAttribute(
        "aria-pressed",
        String(panelStates[panelIndex].selected.has(record.number)),
      );
      row.setAttribute(
        "aria-label",
        `Lap ${record.number}, lap time ${formatTime(record.duration, true)}, total time ${formatTime(record.total, true)}`,
      );
      row.addEventListener("click", function () {
        const selected = panelStates[panelIndex].selected;
        if (selected.has(record.number)) {
          selected.delete(record.number);
        } else {
          selected.add(record.number);
        }
        row.setAttribute("aria-pressed", String(selected.has(record.number)));
        updateSelectionSummary();
      });
    }
    row.innerHTML = `<span class="min-w-0">${record.number}<span class="lap-check" aria-hidden="true">✓</span></span><span class="min-w-0 truncate">${formatTime(record.duration, !isCompact)}</span><span class="min-w-0 truncate">${formatTime(record.total, !isCompact)}</span>`;
    return row;
  }

  function updateSelectionSummary() {
    const countElement = document.getElementById("selectionCount");
    const totalElement = document.getElementById("selectionTotal");
    if (!countElement || !totalElement) return;

    const panel = panelStates[expandedPanelIndex ?? 0];
    let totalTime = 0;
    for (let i = 0, total = panel.records.length; i < total; i++) {
      const record = panel.records[i];
      if (panel.selected.has(record.number)) totalTime += record.duration;
    }
    const count = panel.selected.size;
    countElement.textContent = `${count} ${count === 1 ? "lap" : "laps"} selected`;
    totalElement.textContent = formatTime(totalTime, true);
    totalElement.setAttribute(
      "aria-label",
      `${count} ${count === 1 ? "lap" : "laps"} selected, total ${formatTime(totalTime, true)}`,
    );
  }

  /** @param {MouseEvent} event */
  function handleReviewClick(event) {
    const target = event.target;
    if (!(target instanceof Element) || isRunning) return;
    const expandButton = target.closest("[data-expand-panel]");
    if (expandButton instanceof HTMLElement) {
      expandedPanelIndex = Number(expandButton.dataset.expandPanel);
      isSelectingLaps = panelStates[expandedPanelIndex].records.length > 0;
      renderLapArea();
      document.getElementById("backToTables")?.focus();
    } else if (target.closest("#backToTables")) {
      const previousPanel = expandedPanelIndex;
      expandedPanelIndex = null;
      isSelectingLaps = false;
      renderLapArea();
      const previousButton = tapArea.querySelector(
        `[data-expand-panel="${previousPanel}"]`,
      );
      if (previousButton instanceof HTMLElement) previousButton.focus();
    } else if (target.closest("#sumLaps")) {
      isSelectingLaps = !isSelectingLaps;
      renderLapArea();
      document.getElementById("sumLaps")?.focus();
    } else if (target.closest("#clearSelection")) {
      panelStates[expandedPanelIndex ?? 0].selected.clear();
      const rows = tapArea.querySelectorAll(".lap-selectable");
      for (let i = 0, total = rows.length; i < total; i++) {
        rows[i].setAttribute("aria-pressed", "false");
      }
      updateSelectionSummary();
    }
  }

  // Format time in milliseconds to display string (MM:SS:ms)
  /**
   * @param {number} timeInMs
   * @param {boolean} showFullPrecision
   * @returns {string}
   */
  function formatTime(timeInMs, showFullPrecision = false) {
    const minutes = Math.floor(timeInMs / 60000);
    const seconds = Math.floor((timeInMs % 60000) / 1000);
    const minutesText = String(minutes).padStart(2, "0");
    const secondsText = String(seconds).padStart(2, "0");
    let result = "";

    if (showFullPrecision) {
      // For lap records, show milliseconds (3 decimal places)
      const milliseconds = Math.floor(timeInMs % 1000);
      const millisecondsText = String(milliseconds).padStart(3, "0");
      result = `${minutesText}:${secondsText}.${millisecondsText}`;
      return result;
    }

    // For main display, only show centiseconds (tens of ms)
    const centiseconds = Math.floor((timeInMs % 1000) / 10);
    const centisecondsText = String(centiseconds).padStart(2, "0");
    result = `${minutesText}:${secondsText}.${centisecondsText}`;
    return result;
  }

  // Update the display with current time
  function updateDisplay() {
    if (isRunning) {
      const currentTime = performance.now();
      const currentElapsed = elapsedTime + (currentTime - startTime);
      display.textContent = formatTime(currentElapsed, false);
      animationFrameId = requestAnimationFrame(updateDisplay);
    }
  }

  /**
   * @returns {HTMLVideoElement}
   */
  function createKeepAwakeVideo() {
    if (keepAwakeVideo) {
      return keepAwakeVideo;
    }

    const videoElement = document.createElement("video");
    videoElement.setAttribute("aria-hidden", "true");
    videoElement.setAttribute("playsinline", "");
    videoElement.muted = true;
    videoElement.loop = true;
    videoElement.preload = "auto";
    videoElement.style.position = "fixed";
    videoElement.style.width = "1px";
    videoElement.style.height = "1px";
    videoElement.style.opacity = "0";
    videoElement.style.pointerEvents = "none";
    videoElement.style.top = "0";
    videoElement.style.left = "0";
    videoElement.src =
      "data:video/mp4;base64,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";
    document.body.appendChild(videoElement);
    keepAwakeVideo = videoElement;
    return keepAwakeVideo;
  }

  function hasActiveWakeLock() {
    let result = false;
    if (wakeLock && !wakeLock.released) {
      result = true;
    }
    return result;
  }

  /**
   * @param {HTMLVideoElement | null} videoElement
   * @returns {void}
   */
  function releaseVideoWakeFallback(videoElement = keepAwakeVideo) {
    if (keepAwakeVideo === videoElement) {
      keepAwakeVideo = null;
    }

    if (!videoElement) {
      return;
    }

    videoElement.pause();
    videoElement.removeAttribute("src");
    videoElement.load();
    videoElement.remove();
  }

  /**
   * @returns {Promise<boolean>}
   */
  async function requestVideoWakeFallback() {
    const videoElement = createKeepAwakeVideo();
    let result = false;

    if (!videoElement.paused) {
      result = true;
      return result;
    }

    try {
      await videoElement.play();
      if (!shouldKeepAwake || document.visibilityState !== "visible") {
        releaseVideoWakeFallback(videoElement);
        return result;
      }

      console.log("Wake lock active: hidden video fallback");
      result = true;
    } catch (error) {
      // iOS may require this to run from a user gesture.
      console.log(
        "Wake lock pending: hidden video fallback needs a user gesture",
        error,
      );
    }

    return result;
  }

  /**
   * @param {WakeLockSentinel} releasedWakeLock
   * @returns {void}
   */
  function handleNativeWakeLockRelease(releasedWakeLock) {
    if (wakeLock !== releasedWakeLock) {
      return;
    }

    wakeLock = null;

    if (!shouldKeepAwake) {
      return;
    }

    if (document.visibilityState !== "visible") {
      return;
    }

    window.setTimeout(function () {
      void requestWakeLock();
    }, 0);
  }

  /**
   * @returns {Promise<boolean>}
   */
  async function requestNativeWakeLock() {
    let result = false;

    if (!("wakeLock" in navigator)) {
      return result;
    }

    try {
      const nextWakeLock = await navigator.wakeLock.request("screen");
      if (!shouldKeepAwake || document.visibilityState !== "visible") {
        await nextWakeLock.release();
        return result;
      }

      wakeLock = nextWakeLock;
      wakeLock.addEventListener("release", function () {
        handleNativeWakeLockRelease(nextWakeLock);
      });
      console.log("Wake lock active: native Screen Wake Lock API");
      result = true;
    } catch (error) {
      wakeLock = null;
      console.log(
        "Wake lock unavailable: native request failed, using fallback",
        error,
      );
    }

    return result;
  }

  async function requestWakeLock() {
    if (wakeLockRequestPromise) {
      await wakeLockRequestPromise;
      return;
    }

    wakeLockRequestPromise = requestWakeLockOnce();
    try {
      await wakeLockRequestPromise;
    } finally {
      wakeLockRequestPromise = null;
    }
  }

  async function requestWakeLockOnce() {
    if (!shouldKeepAwake) {
      return;
    }

    if (document.visibilityState !== "visible") {
      return;
    }

    if (hasActiveWakeLock()) {
      return;
    }

    const isNativeWakeLockActive = await requestNativeWakeLock();
    if (isNativeWakeLockActive) {
      releaseVideoWakeFallback(keepAwakeVideo);
      return;
    }

    if (!shouldKeepAwake || document.visibilityState !== "visible") {
      return;
    }

    await requestVideoWakeFallback();
  }

  function refreshWakeLock() {
    if (document.visibilityState !== "visible") {
      disableWakeLock();
      return;
    }

    enableWakeLock();
  }

  function enableWakeLock() {
    shouldKeepAwake = true;
    void requestWakeLock();
  }

  function disableWakeLock() {
    shouldKeepAwake = false;
    void releaseWakeLock();
  }

  async function releaseWakeLock() {
    const activeWakeLock = wakeLock;
    const activeKeepAwakeVideo = keepAwakeVideo;
    wakeLock = null;

    if (activeWakeLock && !activeWakeLock.released) {
      try {
        await activeWakeLock.release();
      } catch (error) {
        // The browser may have already released it during page teardown.
      }
    }

    if (!shouldKeepAwake) {
      releaseVideoWakeFallback(activeKeepAwakeVideo);
    }
  }

  /**
   * @param {boolean} isDisabled
   * @returns {void}
   */
  function setResetDisabled(isDisabled) {
    reset.disabled = isDisabled;
    reset.style.pointerEvents = isDisabled ? "none" : "";
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  function handleResetDebugTap(event) {
    if (!reset.disabled) {
      debugTapState.count = 0;
      return;
    }

    const isInsideResetButton = isPointerInsideElement(event, reset);
    if (!isInsideResetButton) {
      return;
    }

    event.preventDefault();
    void handleDebugTap();
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  function resetDebugTapStateWhenOutsideReset(event) {
    if (!reset.disabled) {
      debugTapState.count = 0;
      return;
    }

    const isInsideResetButton = isPointerInsideElement(event, reset);
    if (isInsideResetButton) {
      return;
    }

    debugTapState.count = 0;
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} element
   * @returns {boolean}
   */
  function isPointerInsideElement(event, element) {
    const bounds = element.getBoundingClientRect();
    const isInsideX =
      event.clientX >= bounds.left && event.clientX <= bounds.right;
    const isInsideY =
      event.clientY >= bounds.top && event.clientY <= bounds.bottom;
    let result = false;

    if (isInsideX && isInsideY) {
      result = true;
    }

    return result;
  }

  async function handleDebugTap() {
    const now = Date.now();
    const withinWindow = now - debugTapState.lastTapAt <= debugTapWindowMs;
    debugTapState.count = withinWindow ? debugTapState.count + 1 : 1;
    debugTapState.lastTapAt = now;

    if (debugTapState.count < debugTapThreshold) {
      return;
    }

    debugTapState.count = 0;
    await showDebugInfo();
  }

  async function showDebugInfo() {
    const serviceWorkerState = await getServiceWorkerState();
    const standalone = isStandalonePwa();
    const wakeLockPermission = await getWakeLockPermissionState();
    const debugItems = [
      { label: "Online", value: String(navigator.onLine) },
      { label: "Secure context", value: String(window.isSecureContext) },
      { label: "PWA standalone", value: String(standalone) },
      { label: "Visibility", value: document.visibilityState },
      { label: "SW", value: serviceWorkerState },
      { label: "Wake lock API", value: String("wakeLock" in navigator) },
      { label: "Wake lock permission", value: wakeLockPermission },
      { label: "Wake lock requested", value: String(shouldKeepAwake) },
      { label: "Wake lock active", value: String(hasActiveWakeLock()) },
      {
        label: "Wake lock pending",
        value: String(wakeLockRequestPromise !== null),
      },
      {
        label: "Wake lock released",
        value: wakeLock ? String(wakeLock.released) : "(no sentinel)",
      },
      { label: "Video fallback", value: getVideoWakeFallbackState() },
      { label: "Running", value: String(isRunning) },
      { label: "Elapsed ms", value: String(Math.round(elapsedTime)) },
      { label: "URL", value: window.location.href },
      { label: "UA", value: navigator.userAgent },
    ];

    const rows = [];
    for (let i = 0, total = debugItems.length; i < total; i++) {
      const item = debugItems[i];
      rows.push(htl`
        <div class="dialog-debug-row">
          <div class="dialog-debug-label">${item.label}</div>
          <div class="dialog-debug-value">${item.value}</div>
        </div>
      `);
    }

    const content = htl`
      <div class="dialog-rich-content">
        <div class="dialog-rich-title">Debug Info</div>
        <div class="dialog-debug-list">${rows}</div>
      </div>
    `;

    await dAlert({ message: content });
  }

  /**
   * @returns {Promise<string>}
   */
  async function getServiceWorkerState() {
    let serviceWorkerState = "(unsupported)";

    try {
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        serviceWorkerState = getRegistrationState(registration);
      }
    } catch (error) {
      serviceWorkerState = `(getRegistration failed: ${getErrorMessage(error)})`;
    }

    return serviceWorkerState;
  }

  /**
   * @param {ServiceWorkerRegistration | undefined} registration
   * @returns {string}
   */
  function getRegistrationState(registration) {
    let registrationState = "(no registration)";

    if (registration !== undefined) {
      const active = registration.active ? registration.active.state : "none";
      const waiting = registration.waiting
        ? registration.waiting.state
        : "none";
      const installing = registration.installing
        ? registration.installing.state
        : "none";
      registrationState = `active=${active} waiting=${waiting} installing=${installing}`;
    }

    return registrationState;
  }

  /**
   * @returns {Promise<string>}
   */
  async function getWakeLockPermissionState() {
    let result = "(unsupported)";

    if (!("permissions" in navigator)) {
      return result;
    }

    try {
      /** @type {PermissionDescriptor} */
      const descriptor = { name: "screen-wake-lock" };
      const permissionStatus = await navigator.permissions.query(descriptor);
      result = permissionStatus.state;
    } catch (error) {
      result = `(query failed: ${getErrorMessage(error)})`;
    }

    return result;
  }

  /**
   * @returns {string}
   */
  function getVideoWakeFallbackState() {
    let result = "(not created)";

    if (keepAwakeVideo) {
      result = keepAwakeVideo.paused ? "paused" : "playing";
    }

    return result;
  }

  /**
   * @returns {boolean}
   */
  function isStandalonePwa() {
    let result = false;

    if (window.matchMedia) {
      result = window.matchMedia("(display-mode: standalone)").matches;
    }

    if ("standalone" in navigator && navigator.standalone === true) {
      result = true;
    }

    return result;
  }

  /**
   * Formats arbitrary values thrown by browser APIs at the error boundary.
   * @param {unknown} error
   * @returns {string}
   */
  function getErrorMessage(error) {
    let message = String(error);

    if (error instanceof Error) {
      message = error.message;
    }

    return message;
  }

  function isChronoIdle() {
    return !isRunning;
  }

  // Start/stop the stopwatch
  function toggleStartStop() {
    if (isRunning) {
      // Stop
      isRunning = false;
      elapsedTime += performance.now() - startTime;
      startStop.textContent = "Start";
      lap.disabled = true;
      setResetDisabled(false);
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      display.textContent = formatTime(elapsedTime, false);
      renderLapArea();
      return;
    }

    // Start
    expandedPanelIndex = null;
    isSelectingLaps = false;
    for (let i = 0, total = panelStates.length; i < total; i++) {
      panelStates[i].selected.clear();
    }
    isRunning = true;
    renderLapArea();
    startTime = performance.now();
    startStop.textContent = "Stop";
    lap.disabled = false;
    setResetDisabled(true);
    updateDisplay();
  }

  /** @returns {number} */
  function getTotalElapsed() {
    return Math.floor(elapsedTime + (performance.now() - startTime));
  }

  // Record a lap
  /**
   * @param {number} panelIndex
   * @param {number} totalElapsed
   */
  function recordLap(panelIndex = 0, totalElapsed = getTotalElapsed()) {
    if (!isRunning) return;

    const panelState = panelStates[panelIndex];
    if (!panelState) return;

    const panelLaps = panelState.lapsElement;
    if (!panelLaps) return;

    const lapTime = totalElapsed - panelState.lastLapTime;
    panelState.lastLapTime = totalElapsed;
    panelState.lapCounter++;

    // Remove "no laps" message if it exists
    const noLapsMessage =
      panelLaps.getElementsByClassName("no-laps-message")[0];
    if (noLapsMessage) {
      noLapsMessage.remove();
    }

    const record = {
      number: panelState.lapCounter,
      duration: lapTime,
      total: totalElapsed,
    };
    panelState.records.push(record);
    const lapElement = createLapRow(panelIndex, record);

    // Add to container (newest laps at the top, after the header)
    if (panelLaps.firstChild) {
      panelLaps.insertBefore(lapElement, panelLaps.firstChild);
    } else {
      panelLaps.appendChild(lapElement);
    }
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  function trackLapTapStart(event) {
    if (!isRunning && elapsedTime > 0) return;
    if (event.target instanceof Element && event.target.closest("button"))
      return;
    if (isSplitMode) {
      handleGridLapPress(event);
      return;
    }

    lapTapStarts.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  function handleLapTapEnd(event) {
    if (isSplitMode) {
      return;
    }

    const tapStart = lapTapStarts.get(event.pointerId);
    lapTapStarts.delete(event.pointerId);

    if (!tapStart) {
      return;
    }

    const movedX = Math.abs(event.clientX - tapStart.x);
    const movedY = Math.abs(event.clientY - tapStart.y);
    const tapMovement = movedX + movedY;
    if (tapMovement > 12) return;

    handleLapPress(event);
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  function clearLapTapStart(event) {
    lapTapStarts.delete(event.pointerId);
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  function handleGridLapPress(event) {
    event.preventDefault();
    handleLapPress(event);
  }

  /**
   * @param {PointerEvent} event
   * @returns {void}
   */
  function handleLapPress(event) {
    if (!isRunning && elapsedTime > 0) return;
    const panelElement =
      event.target instanceof Element
        ? event.target.closest(".split-panel")
        : null;
    const panelIndexText =
      panelElement instanceof HTMLElement
        ? panelElement.dataset.panelIndex
        : "";
    const panelIndex = Number(panelIndexText || "0");
    const tappedPanel =
      panelElement instanceof HTMLElement ? panelElement : null;

    if (isRunning) {
      flashPanelTap(tappedPanel);
      recordLap(panelIndex);
      return;
    }

    flashPanelTap(tappedPanel);
    toggleStartStop();
  }

  /**
   * @param {HTMLElement | null} panelElement
   * @returns {void}
   */
  function flashPanelTap(panelElement) {
    if (!panelElement) return;

    const previousTimeout = panelFlashTimeouts.get(panelElement);
    if (previousTimeout !== undefined) {
      window.clearTimeout(previousTimeout);
    }

    panelElement.classList.add("bg-blue-100");
    const nextTimeout = window.setTimeout(function () {
      panelElement.classList.remove("bg-blue-100");
      panelFlashTimeouts.delete(panelElement);
    }, 180);
    panelFlashTimeouts.set(panelElement, nextTimeout);
  }

  // Reset the stopwatch
  function resetStopwatch() {
    isRunning = false;
    startTime = 0;
    elapsedTime = 0;
    expandedPanelIndex = null;
    isSelectingLaps = false;
    resetPanelStates();
    display.textContent = "00:00.00";
    startStop.textContent = "Start";
    lap.disabled = true;
    setResetDisabled(true);

    // Clear laps
    renderLapArea();
  }

  function toggleSplitMode() {
    isSplitMode = !isSplitMode;
    splitToggle.classList.toggle("bg-gray-800", isSplitMode);
    splitToggle.classList.toggle("text-white", isSplitMode);
    splitToggle.classList.toggle("border-gray-800", isSplitMode);
    panelStates = isSplitMode ? splitPanelStates : singlePanelStates;
    expandedPanelIndex = null;
    isSelectingLaps = false;
    lapTapStarts.clear();
    renderLapArea();
  }

  // Event listeners
  startStop.addEventListener("click", toggleStartStop);
  lap.addEventListener("click", function () {
    const totalElapsed = getTotalElapsed();
    for (let i = 0, total = panelStates.length; i < total; i++) {
      recordLap(i, totalElapsed);
    }
  });
  splitToggle.addEventListener("click", toggleSplitMode);
  tapArea.addEventListener("click", handleReviewClick);
  tapArea.addEventListener("pointerdown", trackLapTapStart);
  tapArea.addEventListener("pointerup", handleLapTapEnd);
  tapArea.addEventListener("pointercancel", clearLapTapStart);
  resetDebugTarget.addEventListener("pointerup", handleResetDebugTap);
  reset.addEventListener("click", resetStopwatch);
  document.addEventListener("pointerup", resetDebugTapStateWhenOutsideReset);
  document.addEventListener("visibilitychange", refreshWakeLock);
  window.addEventListener("pageshow", enableWakeLock);
  window.addEventListener("focus", enableWakeLock);
  window.addEventListener("pagehide", disableWakeLock);
  window.addEventListener("beforeunload", disableWakeLock);
  document.addEventListener("pointerdown", enableWakeLock);
  document.addEventListener("click", enableWakeLock);
  document.addEventListener("keydown", enableWakeLock);
  renderLapArea();
  enableWakeLock();
  void setupServiceWorkerReloadWhenIdle({ isIdle: isChronoIdle });
}

main();
